import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Badge,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  FileOpen as PermitIcon,
  Add as AddIcon,
  People as UserIcon,
  Security as RoleIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  Apps as AppsIcon,
  Info as InfoIcon,
  PrivacyTip as PrivacyIcon,
  LocalShipping as VehicleIcon,
  Assessment as ReportIcon,
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Receipt as ChalanIcon,
  ArrowForward as ForwardingIcon,
  HelpOutline as HelpIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import './App.css';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SuperAdminProvider } from './context/SuperAdminContext';
import { OrgProvider, useOrg } from './context/OrgContext';
import RtlThemeProvider from './theme/RtlThemeProvider';
import ProtectedRoute from './components/ProtectedRoute';
import SplashScreen from './components/SplashScreen';
import LanguageSwitcher from './components/LanguageSwitcher';
import NotificationCenter from './components/NotificationCenter';
import OnboardingTutorial, { hasSeenOnboarding } from './components/OnboardingTutorial';
import apiClient from './services/apiClient';
import Dashboard from './pages/Dashboard';
import PermitList from './pages/PermitList';
import NewPermit from './pages/NewPermit';
import Reports from './pages/Reports';
import PermitDetails from './pages/PermitDetails';
import PermitEdit from './pages/PermitEdit';
import PermitSearch from './pages/PermitSearch';
import Profile from './pages/Profile';
import ChalanList from './pages/ChalanList';
import CreateChalan from './pages/CreateChalan';
import ChalanDetail from './pages/ChalanDetail';
// Super Admin imports
import SuperAdminLogin from './pages/superadmin/SuperAdminLogin';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import OrganizationManagement from './pages/superadmin/OrganizationManagement';
import SystemConfig from './pages/superadmin/SystemConfig';
import AuditLogs from './pages/superadmin/AuditLogs';
import FeeManagement from './pages/FeeManagement';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import FeatureList from './pages/FeatureList';
import TypesManagement from './pages/TypesManagement';
import ForwardingConfig from './pages/ForwardingConfig';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutUs from './pages/AboutUs';

// ─── Session Timer Component ───────────────────────────────────────
// Set SHOW_SESSION_TIMER to true to display the countdown in the AppBar
const SHOW_SESSION_TIMER = false;

function SessionTimer({ orgPrefix }) {
  const [remaining, setRemaining] = useState(null);
  const { logout } = useAuth();
  const expiredRef = React.useRef(false);

  useEffect(() => {
    const update = () => {
      if (expiredRef.current) return;
      const expiresAt = localStorage.getItem('tokenExpiresAt');
      if (!expiresAt) { setRemaining(null); return; }
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('0:00');
        if (!expiredRef.current) {
          expiredRef.current = true;
          // Use the auth context logout which hits the backend
          logout().then(() => {
            // Fall back to the persisted org code if the `orgPrefix` prop is
            // ever empty, so an expired session doesn't drop the user on the
            // bare, org-less /login (see handleLogout for the same fallback).
            const storedOrgCode = localStorage.getItem('orgCode');
            const targetPrefix = orgPrefix || (storedOrgCode ? `/${storedOrgCode.toLowerCase()}` : '');
            const target = targetPrefix ? `${targetPrefix}/login` : '/login';
            window.location.href = target;
          });
        }
        return;
      }
      const totalSec = Math.floor(diff / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      setRemaining(`${m}:${s.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [logout, orgPrefix]);

  if (!remaining || !SHOW_SESSION_TIMER) return null;
  const isExpired = remaining === '0:00';
  const isLow = !isExpired && parseInt(remaining.split(':')[0]) < 1;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.5, mr: 1,
      px: 1, py: 0.3, borderRadius: 1,
      bgcolor: isExpired ? 'error.main' : isLow ? 'rgba(255,152,0,0.8)' : 'rgba(255,255,255,0.15)',
      fontSize: '0.75rem', fontWeight: 600, color: 'white', lineHeight: 1,
    }}>
      <TimerIcon sx={{ fontSize: 14 }} />
      <span>{remaining}</span>
    </Box>
  );
}

const drawerWidth = 240;
const collapsedDrawerWidth = 90;

// Separate component for Super Admin routes (no main layout)
function SuperAdminRoutes() {
  return (
    <Routes>
      <Route path="login" element={<SuperAdminLogin />} />
      <Route path="dashboard" element={<SuperAdminDashboard />} />
      <Route path="organizations" element={<OrganizationManagement />} />
      <Route path="organizations/:id" element={<OrganizationManagement />} />
      <Route path="organizations/:id/settings" element={<OrganizationManagement />} />
      <Route path="organizations/:id/features" element={<OrganizationManagement />} />
      <Route path="organizations/:id/wizard" element={<OrganizationManagement />} />
      <Route path="organizations/:id/payment" element={<OrganizationManagement />} />
      <Route path="system-config" element={<SystemConfig />} />
      <Route path="audit-logs" element={<AuditLogs />} />
    </Routes>
  );
}

// Reserved top-level auth paths that must never be mistaken for an org code.
// Exported so Login/Register/ForgotPassword can recognize when the `orgCode`
// route param they read via their own useParams() call is actually just the
// auth page's own name (captured by the greedy `/:orgCode/*` pattern when
// visited without a real org prefix, e.g. bare `/login`) rather than a real org.
export const RESERVED_ORG_CODES = ['login', 'register', 'forgot-password'];

// Wrapper component that resolves org from URL param and injects OrgProvider
function OrgAwareRoutes() {
  const { orgCode } = useParams();
  const { isAuthenticated } = useAuth();
  const lower = orgCode?.toLowerCase();

  // A bare `/login`, `/register`, or `/forgot-password` (no org prefix) also
  // matches `/:orgCode/*`, with the word itself captured as `orgCode`. Left
  // unguarded, this sends a bogus tenant lookup (e.g. `/api/org/LOGIN/info/`)
  // and renders a blank page instead of the actual auth screen — this
  // happens whenever a redirect (e.g. logout) lands on a non-prefixed path.
  if (RESERVED_ORG_CODES.includes(lower)) {
    if (isAuthenticated) return <Navigate to="/" />;
    if (lower === 'login') return <Login />;
    if (lower === 'register') return <Register />;
    return <ForgotPassword />;
  }

  return (
    <OrgProvider orgCode={orgCode}>
      <MainAppContent orgPrefix={`/${orgCode}`} />
    </OrgProvider>
  );
}

// Wrapper component that handles routing inside Router context
function AppRouter() {
  const location = useLocation();
  const isSuperAdminRoute = location.pathname.startsWith('/superadmin');

  if (isSuperAdminRoute) {
    return <SuperAdminRoutes />;
  }

  return <MainAppContent orgPrefix="" />;
}

// Sub-routes that don't have their own sidebar entry highlight their parent nav item instead.
const NAV_ROUTE_ALIASES = {
  'permit-details': 'permits',
  'permit-edit': 'permits',
};

function MainAppContent({ orgPrefix = '' }) {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout, orgFeatureEnabled, isAdmin: authIsAdmin, hasFeature: authHasFeature } = useAuth();
  const orgContext = useOrg();
  const orgInfo = orgContext?.org;
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const relativePath = location.pathname.startsWith(orgPrefix)
    ? location.pathname.slice(orgPrefix.length)
    : location.pathname;
  const firstSegment = relativePath.split('/').filter(Boolean)[0] || '';
  const activeSegment = NAV_ROUTE_ALIASES[firstSegment] || firstSegment;
  const activePath = activeSegment ? `${orgPrefix}/${activeSegment}` : `${orgPrefix}/`;
  // Fall back to the persisted org code when `orgPrefix` is empty (i.e. this
  // tree was reached via a bare, non-prefixed URL like plain /login). Without
  // this, logging in from /login races the route's own post-login redirect:
  // Login.js correctly navigates to /krta/, but this component re-renders on
  // the same isAuthenticated flip and its `login`/`register`/`forgot-password`
  // routes below fall back to `${orgPrefix}/` — still "" here — sending the
  // user back to bare "/" and silently dropping the org context.
  const storedOrgCode = localStorage.getItem('orgCode');
  const authRedirectPrefix = orgPrefix || (storedOrgCode ? `/${storedOrgCode.toLowerCase()}` : '');
  const [assignedPermitCount, setAssignedPermitCount] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);

  // Show tutorial automatically on first login
  useEffect(() => {
    if (isAuthenticated && !hasSeenOnboarding()) {
      const timer = setTimeout(() => setShowTutorial(true), 1800);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiClient.get('/permits/stats/')
      .then(res => setAssignedPermitCount(res.data.pendingPermits || 0))
      .catch(() => { });
  }, [isAuthenticated]);

  // Check if user is admin - sourced from AuthContext (single source of truth)
  const isAdmin = authIsAdmin;

  // Check if user has a specific role (falls back gracefully)
  const hasRole = (roleName) => {
    if (!user) return false;
    const userRole = user?.role?.name || user?.role;
    return (userRole || '').toLowerCase() === roleName.toLowerCase();
  };

  // Check if user has a specific feature - sourced from AuthContext
  const hasFeature = authHasFeature;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    // Navigate to the org-specific login page after logout. Fall back to the
    // org code persisted in localStorage if the `orgPrefix` prop is ever
    // empty (e.g. this component rendered via the non-prefixed fallback
    // route) — otherwise the user is dropped on the bare, org-less /login.
    const storedOrgCode = localStorage.getItem('orgCode');
    const targetPrefix = orgPrefix || (storedOrgCode ? `/${storedOrgCode.toLowerCase()}` : '');
    window.location.href = targetPrefix ? `${targetPrefix}/login` : '/login';
  };

  const navigationItems = [
    { label: t('nav.dashboard'), icon: <DashboardIcon />, path: `${orgPrefix}/`, adminOnly: false, requiredFeature: 'dashboard_read' },
    { label: t('nav.viewPermits'), icon: <PermitIcon />, path: `${orgPrefix}/permits`, adminOnly: false, orgFeature: 'permit_read', badge: assignedPermitCount },
    { label: t('nav.newPermit'), icon: <AddIcon />, path: `${orgPrefix}/new-permit`, adminOnly: false, requiredFeature: 'permit_create' },
    { label: t('nav.searchPermits'), icon: <SearchIcon />, path: `${orgPrefix}/search`, adminOnly: false, orgFeature: 'permit_read' },
    { label: t('nav.profile'), icon: <AccountIcon />, path: `${orgPrefix}/profile`, adminOnly: false },
    { label: t('nav.reports'), icon: <ReportIcon />, path: `${orgPrefix}/reports`, requiredFeature: 'report_read', orgFeature: 'report_read' },
    { label: t('nav.chalans'), icon: <ChalanIcon />, path: `${orgPrefix}/chalans`, adminOnly: false, requiredFeature: 'chalan_read', orgFeature: 'chalan_read' },
    { label: t('nav.permitTypes'), icon: <VehicleIcon />, path: `${orgPrefix}/types`, adminOnly: true },
    { label: t('nav.users'), icon: <UserIcon />, path: `${orgPrefix}/users`, adminOnly: true, orgFeature: 'user_read' },
    { label: t('nav.roles'), icon: <RoleIcon />, path: `${orgPrefix}/roles`, adminOnly: true, orgFeature: 'role_read' },
    { label: t('nav.forwarding'), icon: <ForwardingIcon />, path: `${orgPrefix}/forwarding`, adminOnly: true },
  ];

  const infoItems = [
    { label: t('nav.featureList'), icon: <AppsIcon />, path: `${orgPrefix}/features`, adminOnly: true },
    { label: t('nav.aboutUs'), icon: <InfoIcon />, path: `${orgPrefix}/about`, adminOnly: false },
    { label: t('nav.privacyPolicy'), icon: <PrivacyIcon />, path: `${orgPrefix}/privacy`, adminOnly: false },
    { label: t('nav.logout'), icon: <LogoutIcon />, action: 'logout', adminOnly: false },
  ];

  const drawer = (
    <Box sx={{ width: drawerOpen ? drawerWidth : collapsedDrawerWidth, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: drawerOpen ? 2 : 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: drawerOpen ? 'space-between' : 'center', gap: 1 }}>
        {drawerOpen ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src="/logo_pms.png"
              alt="PMS Logo"
              sx={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }}
            />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', lineHeight: 1.2 }}>
                {orgInfo ? orgInfo.name : t('appBar.permitManagementSystem')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {orgInfo ? `${orgInfo.organization_type} ${t('login.portalSuffix')}` : t('appBar.permitManagement')}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            component="img"
            src="/logo_pms.png"
            alt="PMS"
            sx={{ width: 40, height: 40, marginLeft: 2 }}
          />
        )}
        <IconButton
          size="small"
          onClick={() => setDrawerOpen(!drawerOpen)}
          sx={{ ml: drawerOpen ? 'auto' : 0 }}
        >
          {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ flex: 1, overflow: 'auto' }}>
        {navigationItems
          .filter(item => {
            // Org-level feature gate — hides item if org disabled it, user doesn't have the feature, and isn't an admin
            if (item.orgFeature && !orgFeatureEnabled(item.orgFeature) && !hasFeature(item.orgFeature) && !isAdmin) {
              return false;
            }
            // Only show for admins or employees if marked with onlyAdminOrEmployee
            if (item.onlyAdminOrEmployee) {
              return isAdmin || hasFeature('employee');
            }
            // Exclude employees from items marked with excludeEmployees
            if (item.excludeEmployees && hasFeature('employee')) {
              return false;
            }
            if (item.requiredFeature) {
              return hasFeature(item.requiredFeature) || isAdmin;
            }
            if (item.requiredRole) {
              return hasRole(item.requiredRole) || isAdmin;
            }
            return !item.adminOnly || isAdmin;
          })
          .map((item) => {
            const isActive = activePath === item.path;
            const isSubItem = !!item.category; // Check if this is a sub-category item
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component="a"
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileOpen(false);
                    window.history.pushState(null, '', item.path);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }}
                  sx={{
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    borderLeft: (theme) => isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                    pl: isSubItem
                      ? (isActive && drawerOpen ? 3.75 : drawerOpen ? 4 : 1.5)
                      : (isActive && drawerOpen ? 1.75 : drawerOpen ? 2 : 1.5),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    justifyContent: !drawerOpen ? 'center' : 'flex-start',
                    opacity: isSubItem ? 0.85 : 1,
                    '& .MuiListItemIcon-root': {
                      color: isActive ? 'primary.contrastText' : isSubItem ? 'text.disabled' : 'text.secondary',
                      transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      minWidth: drawerOpen ? 40 : 0,
                    },
                    '& .MuiListItemText-primary': {
                      color: isActive ? 'primary.contrastText' : isSubItem ? 'text.secondary' : 'text.primary',
                      fontWeight: isActive ? 700 : 500,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: drawerOpen ? 'block' : 'none',
                    },
                    '&:hover': {
                      backgroundColor: (theme) => isActive
                        ? theme.palette.primary.dark
                        : alpha(theme.palette.primary.main, 0.12),
                      '& .MuiListItemIcon-root': {
                        color: isActive ? 'primary.contrastText' : 'primary.main',
                      },
                    },
                  }}
                  title={!drawerOpen ? item.label : ''}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.badge ? (
                      <Badge badgeContent={item.badge} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : item.icon}
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
      <Divider sx={{ my: 1 }} />
      {drawerOpen && (
        <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
          {t('nav.information')}
        </Typography>
      )}
      <List>
        {infoItems
          .filter(item => {
            // Org-level feature gate
            if (item.orgFeature && !orgFeatureEnabled(item.orgFeature)) {
              return false;
            }
            // Only show for admins or employees if marked with onlyAdminOrEmployee
            if (item.onlyAdminOrEmployee) {
              return isAdmin || hasFeature('employee');
            }
            // Exclude employees from items marked with excludeEmployees
            if (item.excludeEmployees && hasFeature('employee')) {
              return false;
            }
            if (item.requiredFeature) {
              return hasFeature(item.requiredFeature) || isAdmin;
            }
            if (item.requiredRole) {
              return hasRole(item.requiredRole) || isAdmin;
            }
            return !item.adminOnly || isAdmin;
          })
          .map((item) => {
            const isActive = activePath === item.path;
            const handleItemClick = (e) => {
              e.preventDefault();
              if (item.action === 'logout') {
                handleLogout();
              } else {
                setMobileOpen(false);
                window.history.pushState(null, '', item.path);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            };
            return (
              <ListItem key={item.path || item.action} disablePadding>
                <ListItemButton
                  component="a"
                  href={item.path || '#'}
                  onClick={handleItemClick}
                  sx={{
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    borderLeft: (theme) => isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                    pl: isActive && drawerOpen ? 1.75 : drawerOpen ? 2 : 1.5,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    justifyContent: !drawerOpen ? 'center' : 'flex-start',
                    '& .MuiListItemIcon-root': {
                      color: isActive ? 'primary.contrastText' : item.action === 'logout' ? 'error.main' : 'text.secondary',
                      transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      minWidth: drawerOpen ? 40 : 0,
                    },
                    '& .MuiListItemText-primary': {
                      color: isActive ? 'primary.contrastText' : item.action === 'logout' ? 'error.main' : 'text.primary',
                      fontWeight: isActive ? 700 : 500,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: drawerOpen ? 'block' : 'none',
                    },
                    '&:hover': {
                      backgroundColor: (theme) => {
                        if (isActive) return theme.palette.primary.dark;
                        if (item.action === 'logout') return alpha(theme.palette.error.main, 0.12);
                        return alpha(theme.palette.primary.main, 0.12);
                      },
                      '& .MuiListItemIcon-root': {
                        color: isActive ? 'primary.contrastText' : item.action === 'logout' ? 'error.main' : 'primary.main',
                      },
                    },
                  }}
                  title={!drawerOpen ? item.label : ''}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
    </Box>
  );

  return (
    <>
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      <OnboardingTutorial
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        isAdmin={isAdmin}
      />
      <CssBaseline />
      <Box sx={{ display: 'flex', backgroundColor: 'background.default', minHeight: '100vh' }}>
        {isAuthenticated && (
          <>
            <AppBar
              position="fixed"
              sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                width: { xs: '100%', md: `calc(100% - ${drawerOpen ? drawerWidth : collapsedDrawerWidth}px)` },
                ml: { xs: 0, md: `${drawerOpen ? drawerWidth : collapsedDrawerWidth}px` },
                backgroundColor: 'primary.main',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Toolbar>
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: { xs: 0.5, sm: 2 }, display: { md: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
                <Box
                  component="img"
                  src="/logo_pms.png"
                  alt="PMS"
                  sx={{ width: 32, height: 32, objectFit: 'contain', mr: { xs: 0.5, sm: 1 }, flexShrink: 0 }}
                />
                <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {orgContext?.orgCode || storedOrgCode || 'PMS'}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {orgInfo ? `${orgInfo.name} ${t('appBar.portalSuffix')}` : t('appBar.permitManagementSystem')}
                  </Box>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  {SHOW_SESSION_TIMER && <SessionTimer orgPrefix={orgPrefix} />}
                  <LanguageSwitcher color="inherit" />
                  <NotificationCenter />
                  <IconButton
                    onClick={handleMenuOpen}
                    color="inherit"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <AccountIcon sx={{ mr: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, display: { xs: 'none', md: 'block' } }}>
                      {user?.username}
                    </Typography>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem disabled>
                      <Typography variant="body2">
                        <strong>
                          {user?.role?.display_name ||
                            (typeof user?.role === 'string' ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User')}
                        </strong>
                      </Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { handleMenuClose(); setShowTutorial(true); }}>
                      <HelpIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">{t('appBar.helpTutorial')}</Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">{t('nav.logout')}</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </Toolbar>
            </AppBar>

            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: drawerWidth,
                  backgroundColor: 'background.paper',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              {drawer}
            </Drawer>

            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
                  backgroundColor: 'background.paper',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  position: 'fixed',
                  height: '100%',
                  top: 0,
                  left: 0,
                  zIndex: (theme) => theme.zIndex.drawer,
                  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  overflowX: 'hidden',
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            ml: { xs: 0, md: `${drawerOpen ? drawerWidth : collapsedDrawerWidth}px` },
            minHeight: '100vh',
            transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Box component="main" sx={{ flexGrow: 1, pt: 8, px: 0, pb: 0 }}>
            <Routes>
              {/* Auth Routes — relative paths so they work under any /:orgCode/* parent */}
              <Route path="login" element={isAuthenticated ? <Navigate to={`${authRedirectPrefix}/`} /> : <Login />} />
              <Route path="register" element={isAuthenticated ? <Navigate to={`${authRedirectPrefix}/`} /> : <Register />} />
              <Route path="forgot-password" element={isAuthenticated ? <Navigate to={`${authRedirectPrefix}/`} /> : <ForgotPassword />} />

              {/* Protected Routes */}
              <Route
                index
                element={
                  <ProtectedRoute requiredFeature="dashboard_read">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="permits"
                element={
                  <ProtectedRoute orgFeature="permit_read">
                    <PermitList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="new-permit"
                element={
                  <ProtectedRoute requiredFeature="permit_create">
                    <NewPermit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ProtectedRoute requiredFeature="report_read" orgFeature="report_read">
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="search"
                element={
                  <ProtectedRoute orgFeature="permit_read">
                    <PermitSearch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="permit-details/:permitId"
                element={
                  <ProtectedRoute orgFeature="permit_read">
                    <PermitDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="permit-edit/:permitId"
                element={
                  <ProtectedRoute requiredFeature="permit_update">
                    <PermitEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              {/* Chalan Routes */}
              <Route
                path="chalans"
                element={
                  <ProtectedRoute orgFeature="chalan_read">
                    <ChalanList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="chalans/create"
                element={
                  <ProtectedRoute requiredFeature="chalan_create" orgFeature="chalan_read">
                    <CreateChalan />
                  </ProtectedRoute>
                }
              />
              <Route
                path="chalans/:id"
                element={
                  <ProtectedRoute orgFeature="chalan_read">
                    <ChalanDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="fee-management"
                element={
                  <ProtectedRoute orgFeature="chalan_update">
                    <FeeManagement />
                  </ProtectedRoute>
                }
              />

              {/* Info Routes */}
              <Route
                path="features"
                element={
                  <ProtectedRoute>
                    <FeatureList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="about"
                element={
                  <ProtectedRoute>
                    <AboutUs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="privacy"
                element={
                  <ProtectedRoute>
                    <PrivacyPolicy />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes — registered unconditionally so a hard page load/
                  refresh can match them before AuthContext's `loading` flag
                  (and therefore `isAdmin`) resolves. Gating with `{isAdmin &&
                  ...}` here raced that load: on the first render `isAdmin` is
                  still false, so these routes wouldn't exist yet, the `*`
                  catch-all below would match instead, and the user was bounced
                  to the dashboard before `isAdmin` ever became true. Access
                  control now lives entirely in ProtectedRoute's `requireAdmin`,
                  which correctly waits for `loading` first. */}
              <Route
                path="types"
                element={
                  <ProtectedRoute requireAdmin>
                    <TypesManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="users"
                element={
                  <ProtectedRoute requireAdmin orgFeature="user_read">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="roles"
                element={
                  <ProtectedRoute requireAdmin orgFeature="role_read">
                    <RoleManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="forwarding"
                element={
                  <ProtectedRoute requireAdmin>
                    <ForwardingConfig />
                  </ProtectedRoute>
                }
              />

              {/* Redirect unknown routes */}
              <Route path="*" element={<Navigate to={isAuthenticated ? `${orgPrefix}/` : `${orgPrefix}/login`} />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    </>
  );
}

function App() {
  return (
    <RtlThemeProvider>
      <AuthProvider>
        <SuperAdminProvider>
          <Router>
            <Routes>
              {/* Super admin has its own standalone layout */}
              <Route path="/superadmin/*" element={<SuperAdminRoutes />} />
              {/* Public pages — no login or org required (e.g. for App Store URLs) */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/support" element={<AboutUs />} />
              {/* Org-prefixed routes: /rta/login, /pta/dashboard, etc. */}
              <Route path="/:orgCode/*" element={<OrgAwareRoutes />} />
              {/* Legacy no-prefix routes (e.g. plain /login) */}
              <Route path="/*" element={<MainAppContent orgPrefix="" />} />
            </Routes>
          </Router>
        </SuperAdminProvider>
      </AuthProvider>
    </RtlThemeProvider>
  );
}

export default App;